import { type FormatOptions, formatDateTime, formatTime } from '@/lib/datetime';
import { createSignal, onCleanup } from 'solid-js';

/** Runner pages default to Bangkok time while the user's timezone preference is Auto. */
export const BKK: FormatOptions = { fallbackTimeZone: 'Asia/Bangkok' };

export const dateTime = (iso: string | null | undefined): string =>
  iso ? formatDateTime(iso, BKK) : '—';

export const timeOnly = (iso: string | null | undefined): string =>
  iso ? formatTime(iso, BKK) : '—';

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

/** 1536 → "1.5 KB" (binary units). */
export const bytes = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  let v = Math.max(0, n);
  let i = 0;
  while (v >= 1024 && i < UNITS.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${UNITS[i]}`;
};

export const perSecond = (n: number | null | undefined): string => `${bytes(n)}/s`;

export const pct = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : `${n.toFixed(n >= 10 ? 0 : 1)}%`;

/** 3725 → "1h 02m"; 185 → "3m 05s"; 42 → "42s". */
export const duration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || seconds < 0) return '—';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (x: number) => String(x).padStart(2, '0');
  if (h > 0) return `${h}h ${pad(m)}m`;
  if (m > 0) return `${m}m ${pad(s % 60)}s`;
  return `${s}s`;
};

/** Seconds between two instants (end defaults to `now`). */
export const secondsBetween = (startIso: string | null, endIso: string | null, now: number) =>
  startIso ? ((endIso ? Date.parse(endIso) : now) - Date.parse(startIso)) / 1000 : null;

/** A ticking clock so durations and "last seen" advance between refetches. */
export const useNow = (everyMs = 1000) => {
  const [now, setNow] = createSignal(Date.now());
  const timer = setInterval(() => setNow(Date.now()), everyMs);
  onCleanup(() => clearInterval(timer));
  return now;
};
